"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultOptions = void 0;
exports.isJwtToken = isJwtToken;
const tslib_1 = require("tslib");
const events_1 = tslib_1.__importDefault(require("events"));
const deepmerge_1 = tslib_1.__importDefault(require("deepmerge"));
const log_client_1 = require("@video/log-client");
const jwt_decode_1 = require("jwt-decode");
const package_json_1 = tslib_1.__importDefault(require("../../../package-json"));
const adapter_1 = require("../../../api/adapter");
const common_1 = require("../../../utils/common");
exports.defaultOptions = {
    timeout: 30000,
    headers: {
        Accept: "application/json",
    },
};
function isFetchResponse(resp) {
    return resp.token !== undefined || resp.expire !== undefined;
}
function isJwtToken(token) {
    try {
        (0, jwt_decode_1.jwtDecode)(token);
        return true;
    }
    catch (e) {
        return false;
    }
}
/**
 * @class Video authorization client
 */
class Authorization extends events_1.default {
    constructor(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        super();
        this._stopped = false;
        this._fatalError = null;
        this._token = null;
        this._retry = 0;
        this._retries = [
            0, // immediate
            200, // 200ms
            1000, // 1s
            3000, // 3s
        ];
        this._autorefresh = null;
        this._url = null;
        const logger = (_a = options === null || options === void 0 ? void 0 : options.logger) !== null && _a !== void 0 ? _a : new log_client_1.LoggerCore("VDC-core");
        options === null || options === void 0 ? true : delete options.logger;
        const client = (_c = (_b = options === null || options === void 0 ? void 0 : options.loggerConfig) === null || _b === void 0 ? void 0 : _b.clientName) !== null && _c !== void 0 ? _c : "VDC";
        const prevClient = logger.getLoggerMeta("client");
        const prevRelease = logger.getLoggerMeta("release");
        const prevPackage = logger.getLoggerMeta("package");
        // const logContext = {
        //   timeout: this.options.timeout,
        //   headers: this.options.headers,
        //   endpointUri: this.options.endpoint.uri,
        //   bootstrapToken: this.options.bootstrap.token,
        //   bootstrapRefresh: this.options?.bootstrap?.refreshToken,
        // };
        // this.logger = new Logger({
        //   registerPackage: {
        //     name: packageName,
        //     id: uuid().slice(0, 8),
        //     context: logContext,
        //   },
        // });
        logger
            .setLoggerMeta("package", prevPackage != null && prevPackage !== "VDC-core" ? `${prevPackage}/VDC-core` : "VDC-core")
            .setLoggerMeta("client", prevClient != null ? `${prevClient}/${client}` : client)
            .setLoggerMeta("release", prevRelease != null ? `${prevRelease}/${package_json_1.default.version}` : package_json_1.default.version)
            .setLoggerMeta("commitHash", package_json_1.default.commit)
            .setLoggerMeta("contextId", (_d = (0, common_1.contextId)()) !== null && _d !== void 0 ? _d : "")
            .setLoggerMeta("instanceId", (_e = (0, common_1.instanceId)()) !== null && _e !== void 0 ? _e : "")
            .appendChain(Authorization);
        // Do we need this??
        // const support = new Support(new LoggerCore("VDC-core").extend(logger).appendChain(Support));
        this.logger = logger;
        this.options = (0, deepmerge_1.default)(exports.defaultOptions, options);
        if (options.endpoint == null && (options === null || options === void 0 ? void 0 : options.bootstrap) == null) {
            this.validationError("endpoint or bootstrap must be configured for auth");
            return;
        }
        if (((_f = this.options.bootstrap) === null || _f === void 0 ? void 0 : _f.token) != null) {
            this._token = this.options.bootstrap.token;
            this.logger.setMessageAggregate("token", (_g = this._token) !== null && _g !== void 0 ? _g : "undefined");
            this.logger.info("auth token set from bootstrap value");
        }
        this._headers = this.options.headers;
        if (this._token == null) {
            this._retryRequest();
        }
        else if (((_h = this.options.bootstrap) === null || _h === void 0 ? void 0 : _h.expire) != null) {
            const retryAfter = this.options.bootstrap.expire.getTime() - Date.now() - 60 * 1000;
            this._autorefresh = adapter_1.device.setTimeout(() => {
                this._autorefresh = null;
                if (!this._stopped) {
                    this._retryRequest();
                }
            }, retryAfter);
        }
    }
    async requestAsync() {
        return new Promise((resolve, reject) => {
            this.request((err, token, isJwt) => {
                if (err != null) {
                    reject(err);
                    return;
                }
                resolve([token !== null && token !== void 0 ? token : null, isJwt !== null && isJwt !== void 0 ? isJwt : false]);
            });
        });
    }
    validationError(msg) {
        const e = new Error(msg);
        this._fatalError = e;
        this._stopped = true;
        this.logger.error(msg, {
            errName: e === null || e === void 0 ? void 0 : e.name,
            errStack: e === null || e === void 0 ? void 0 : e.stack,
        });
    }
    destroy() {
        this.logger.info("destroy()");
        this._stopped = true;
    }
    /**
     * This callback will execute a request which requires the auth token. The token will be available as the second argument if no error was returned.
     * @callback cbRequest
     * @param {Error} e - Returns a timeout error (Error('authorization: timeout')) or a fatal error
     * @param {string} token - The authorization bearer token to use in Video API requests
     */
    /**
     * Execute a request which requires an authorization token when the token is available
     * @param {cbRequest} cb - The callback that executes the request.
     */
    request(cb) {
        if (this._fatalError) {
            cb(this._fatalError);
            return;
        }
        if (this._token != null) {
            this.logger.debug("existing token returned on token request");
            cb(null, this._token, isJwtToken(this._token));
            return;
        }
        const tokenPass = () => {
            this.removeListener("fatal", tokenFatal);
            adapter_1.device.clearTimeout(requestTimeout);
            this.logger.debug("new token returned on token request");
            cb(null, this._token, isJwtToken(this._token));
        };
        const tokenFatal = () => {
            this.removeListener("token", tokenPass);
            adapter_1.device.clearTimeout(requestTimeout);
            this.logger.debug("new token returned on token request");
            cb(this._fatalError);
        };
        const requestTimeout = adapter_1.device.setTimeout(() => {
            this.removeListener("token", tokenPass);
            this.removeListener("fatal", tokenFatal);
            this.logger.info("token request timeout");
            cb(new Error("authorization: timeout"));
        }, this.options.timeout);
        this.once("token", tokenPass);
        this.once("fatal", tokenFatal);
    }
    /**
     * Should be called if a Video endpoint returned an 401 error. A new request will be issued to the authorization server's endpoint to get a new token.
     */
    refreshToken(token) {
        var _a;
        this.logger.debug("refreshToken called", {
            token,
        });
        if (token == null && this._token == null) {
            this.logger.debug("no token to refresh");
            return false;
        }
        if (token != null) {
            this._token = token;
            this.logger.setMessageAggregate("token", (_a = this._token) !== null && _a !== void 0 ? _a : "undefined");
            this.logger.info("token manually set from refreshToken");
            this._fatalError = null;
            if (this._autorefresh != null) {
                adapter_1.device.clearTimeout(this._autorefresh);
                this._autorefresh = null;
            }
            return true;
        }
        if (this._fatalError) {
            return false;
        }
        if (this._token != null) {
            if (this._autorefresh != null) {
                adapter_1.device.clearTimeout(this._autorefresh);
                this._autorefresh = null;
            }
            this._token = null;
            this.logger.removeMessageAggregate("token");
            (() => {
                this._retryRequest();
            })();
        }
        return true;
    }
    /**
     * Check if token has been recieved by Auth
     */
    ensureToken(cb) {
        if (this._token == null) {
            this.once("token", () => {
                cb();
            });
            return;
        }
        cb();
    }
    /**
     * Custom parse json
     * "node-fetch" version different than client fetch
     */
    _parseJSON(response) {
        return response.text().then((text) => {
            if (text) {
                try {
                    return JSON.parse(text);
                }
                catch (e) {
                    // @todo  what do we want to do here?
                    // e.fatal = true;
                    // throw e;
                }
            }
            return {};
        });
    }
    /**
     * Make a request or retry after the current retry number's delay
     * @ignore
     */
    _retryRequest() {
        const retry = this._retries[this._retry];
        if (retry) {
            setTimeout(() => {
                this._makeRequest();
            }, retry);
            return;
        }
        this._makeRequest();
    }
    /**
     * Not sure what this is supposed to do.
     * */
    toJSON() {
        return {};
    }
    _tokenSuccess(token) {
        this._token = token;
        this.logger.setMessageAggregate("token", this._token);
        this._retry = 0;
        setTimeout(() => {
            this.emit("token", {
                token,
            });
        });
    }
    /**
     * Make a request to the authorization server.
     * @ignore
     */
    async _makeRequest() {
        var _a, _b, _c;
        if (((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.bootstrap) === null || _b === void 0 ? void 0 : _b.refreshToken) != null) {
            try {
                const token = await this.options.bootstrap.refreshToken();
                this._tokenSuccess(token);
                this.logger.info("token set from async bootstrap refreshToken");
                return;
            }
            catch (err) {
                if (err instanceof Error) {
                    this._fatalError = err;
                    this.logger.warn("unable to refresh token from bootstrap function", {
                        errName: err === null || err === void 0 ? void 0 : err.name,
                        errStack: err === null || err === void 0 ? void 0 : err.stack,
                    });
                    this.emit("fatal");
                }
                return;
            }
        }
        if (((_c = this.options) === null || _c === void 0 ? void 0 : _c.endpoint) == null) {
            throw new Error("authorization: no ability to refresh token (boostrap or endpoint)");
        }
        if (this.options.endpoint.uri.indexOf("?") === -1) {
            this._url = `${this.options.endpoint.uri}?scope=${this.options.endpoint.scope}`;
        }
        else {
            this._url = `${this.options.endpoint.uri}&scope=${this.options.endpoint.scope}`;
        }
        this._handleFetch();
    }
    async _handleFetch() {
        if (this._url == null) {
            return;
        }
        try {
            const response = await adapter_1.device.fetch(this._url, {
                credentials: "same-origin",
                headers: this._headers,
            });
            this._handleFetchResponse(response);
        }
        catch (err) {
            this._token = null;
            this.logger.removeMessageAggregate("token");
            if (err instanceof TypeError) {
                this._fatalError = err;
                this.logger.error(err.message, {
                    errName: err === null || err === void 0 ? void 0 : err.name,
                    errStack: err === null || err === void 0 ? void 0 : err.stack,
                });
                this.emit("fatal");
                return;
            }
            this.logger.network("unable to refresh token from endpoint", {
            // err,
            // Parse error correctly
            });
            this._retryRequest();
            if (this._retry < this._retries.length - 1) {
                this._retry++;
            }
        }
    }
    _handleFetchResponse(response) {
        if (response.status >= 200 && response.status < 300) {
            const json = this._parseJSON(response);
            if (isFetchResponse(json)) {
                if ((json === null || json === void 0 ? void 0 : json.token) != null) {
                    this._tokenSuccess(json.token);
                    this.logger.info("token set from request response");
                }
                if ((json === null || json === void 0 ? void 0 : json.expire) != null) {
                    // re-request a minute before expiration
                    const retryAfter = new Date(json.expire).getTime() - Date.now() - 60 * 1000;
                    this._autorefresh = adapter_1.device.setTimeout(() => {
                        this._autorefresh = null;
                        if (!this._stopped) {
                            this.logger.info("refreshing token before expiration");
                            this._retryRequest();
                        }
                    }, retryAfter);
                }
            }
            return;
        }
        if (response.status === 401) {
            const e = new Error("authorization: unauthorized");
            // e.fatal = true;
            throw e;
        }
        if (response.status === 403) {
            const e = new Error("authorization: forbidden");
            // e.fatal = true;
            throw e;
        }
        throw new Error("authorization: non-200 response code from auth server");
    }
}
Authorization.displayName = "Authorization";
exports.default = Authorization;
//# sourceMappingURL=auth-core.js.map